import { ThunkDispatch } from "core-integration/src/store";
import { getFutureEvents } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { IRules } from "core-integration/src/store/game/types";
import { getCreateH2HLeagueError } from "core-integration/src/store/leagues/reducers";
import { createH2HLeague } from "core-integration/src/store/leagues/thunks";
import { ICreateH2HLeagueData } from "core-integration/src/store/leagues/types";
import range from "lodash/range";
import React, { useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import Button from "../Button";
import Copy from "../Copy";
import { FieldWrap, InputField, SelectField } from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Title from "../Title";

type Props = WithTranslation;

interface IState {
  koRounds: number;
  maxEntries: number;
  name: string;
  startEvent: number;
}

const CreateH2H = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const error = useSelector(getCreateH2HLeagueError);
  const events = useSelector(getFutureEvents);
  const rules = useSelector(getRules) as IRules;
  const requiredFields: Array<keyof IState> = ["name", "startEvent"];

  const [state, setState] = useState<IState>({
    koRounds: 0,
    maxEntries: 0,
    name: "",
    startEvent: events.length ? events[0].id : 1,
  });

  const maximumKoRounds = () => {
    const remainingEvents = events.length;
    if (remainingEvents <= 1) {
      return 0;
    }
    return Math.min(
      rules.league_max_ko_rounds_private_h2h,
      remainingEvents - 1,
    );
  };

  const minimumSize = (koRounds: number) =>
    state.koRounds ? Math.pow(2, koRounds) : 2;

  const validStartingEvents = (koRounds: number) =>
    koRounds ? events.slice(0, -koRounds) : events;

  const apiDataFromState = (): ICreateH2HLeagueData => ({
    ko_rounds: state.koRounds || null,
    max_entries: state.maxEntries || null,
    name: state.name,
    start_event: state.startEvent,
  });

  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, name: e.currentTarget.value });

  const handleStartEventChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, startEvent: parseInt(e.currentTarget.value, 10) });

  const handleMaxEntriesChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, maxEntries: parseInt(e.currentTarget.value, 10) });

  const handleKoRoundsChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const koRounds = parseInt(e.currentTarget.value, 10);
    const minimumSizeKoRounds = minimumSize(koRounds);
    const events = validStartingEvents(koRounds);
    setState({
      ...state,
      koRounds,
      maxEntries: state.maxEntries
        ? Math.max(minimumSizeKoRounds, state.maxEntries)
        : 0,
      startEvent: Math.min(events[events.length - 1].id, state.startEvent),
    });
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    await dispatch(createH2HLeague(apiDataFromState()));
    navigate("/leagues");
  };

  if (!events.length) {
    return null;
  }
  const disabled = requiredFields.some((e) => !state[e]);
  return (
    <Wrapper>
      <Main>
        <Copy>
          <Title>
            {t("createH2H.title", "Create a new head-to-head league")}
          </Title>
          {error ? (
            error.badRequest &&
            error.badRequest.non_field_errors &&
            error.badRequest.non_field_errors[0].code ===
              "max_private_entries" ? (
              <p>
                {t(
                  "createH2H.errors.maxPrivateEntry",
                  "You may only enter {{ maxPrivateEntries }} private leagues",
                  {
                    maxPrivateEntries: rules && rules.league_join_private_max,
                  },
                )}
              </p>
            ) : (
              <p>{JSON.stringify(error)}</p>
            )
          ) : (
            ""
          )}
          <form onSubmit={handleSubmit}>
            <FieldWrap>
              <InputField
                id="ismLeagueName"
                label={t("createH2H.leagueName.label", "League name")}
                value={state.name}
                onChange={handleNameChange}
                hint={t("createH2H.leagueName.hint", "Maximum 30 characters")}
                maxLength={30}
              />
            </FieldWrap>
            <FieldWrap>
              <SelectField
                id="ismLeagueStartEvent"
                value={state.startEvent}
                onChange={handleStartEventChange}
                label={t("createH2H.startEventLabel", "Scoring starts")}
              >
                {validStartingEvents(state.koRounds).map((e) => (
                  <option
                    value={e.id}
                    aria-selected={e.id === state.startEvent}
                    key={e.id}
                  >
                    {e.name}
                  </option>
                ))}
              </SelectField>
            </FieldWrap>
            <FieldWrap>
              <SelectField
                id="ismLeagueMaxSize"
                value={state.maxEntries}
                onChange={handleMaxEntriesChange}
                label={t("createH2H.maxSize.label", "Maximum size")}
              >
                <option value={0} aria-selected={!state.maxEntries}>
                  {t("createH2H.maxSize.unlimited", "Unlimited")}
                </option>
                {range(
                  minimumSize(state.koRounds),
                  rules.league_max_size_private_h2h + 1,
                ).map((num) => (
                  <option
                    value={num}
                    aria-selected={num === state.maxEntries}
                    key={num}
                  >
                    {num}
                  </option>
                ))}
              </SelectField>
            </FieldWrap>
            {maximumKoRounds() > 0 && (
              <FieldWrap>
                <SelectField
                  id="ismLeagueKoRounds"
                  value={state.koRounds}
                  onChange={handleKoRoundsChange}
                  label={t("createH2H.koRounds.label", "Knockout rounds")}
                >
                  <option value={0} aria-selected={!state.koRounds}>
                    {t("createH2H.maxSize.none", "None")}
                  </option>
                  {range(1, maximumKoRounds() + 1).map((num) => (
                    <option
                      value={num}
                      aria-selected={num === state.koRounds}
                      key={num}
                    >
                      {num}
                    </option>
                  ))}
                </SelectField>
              </FieldWrap>
            )}
            <FieldWrap>
              <Button type="submit" disabled={disabled}>
                {t("createH2H.createLeagues", "Create league")}
              </Button>
            </FieldWrap>
          </form>
        </Copy>
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(CreateH2H);
